import {
  CalendarDays,
  Clock,
  Copy,
  Headphones,
  Instagram,
  PartyPopper,
  Pencil,
  Wallet,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAttractions } from '@/hooks/use-attractions';
import { useUsers } from '@/hooks/use-users';
import { useAuthStore } from '@/stores/auth-store';
import { fmtCouvert, fmtDateBR, fmtTime, fmtWeekday } from '@/lib/dates';
import { copyToClipboard } from '@/lib/clipboard';
import { formatPromoTextForCard } from '@/lib/promos';
import type { EventWithTasks } from '@/hooks/use-events';

async function copyPromos(text: string) {
  if (!text) return;
  const ok = await copyToClipboard(text);
  if (ok) toast.success('Promoções copiadas no formato do card');
  else toast.error('Não foi possível copiar');
}

interface EventViewModalProps {
  open: boolean;
  event: EventWithTasks | null;
  onClose: () => void;
  onEdit: () => void;
}

export function EventViewModal({ open, event, onClose, onEdit }: EventViewModalProps) {
  const me = useAuthStore((s) => s.user);
  const { data: users = [] } = useUsers();
  const { data: attractions = [] } = useAttractions();

  if (!event) return null;

  const canEdit = !!me && (me.role === 'admin' || me.id === event.created_by);
  const creator = users.find((u) => u.id === event.created_by);
  const isDraft = event.status === 'draft';
  const lineup = event.attractions?.filter((a) => a.name) ?? [];
  // Mesmo texto no <pre> e no Copiar: selecionar na mão cola igual no Photoshop.
  const promos = formatPromoTextForCard(event.promos);

  return (
    // Sem header proprio do sheet: o prototipo desenha o badge + X no corpo
    <BottomSheet open={open} onClose={onClose} hideClose>
      {/* Topo: badge de status + botao fechar redondo */}
      <div className="flex items-center justify-between mb-3.5">
        <Badge variant={isDraft ? 'outline' : 'hoje'}>
          {isDraft ? 'rascunho' : 'publicado'}
        </Badge>
        <button
          onClick={onClose}
          aria-label="Fechar"
          className="grid h-8 w-8 place-items-center rounded-full bg-surface-2 text-muted-foreground transition active:scale-[0.97] hover:bg-muted"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Titulo do evento (uppercase display 24px) */}
      <h2 className="u-event-title text-[24px] text-foreground mb-[18px]">{event.name}</h2>

      {/* Grid 2x2 de detalhes */}
      <div className="grid grid-cols-2 gap-2.5 mb-[22px]">
        <DetailBox icon={<Clock className="h-[11px] w-[11px]" />} label="Abre" value={fmtTime(event.open_time) || '—'} />
        <DetailBox icon={<Clock className="h-[11px] w-[11px]" />} label="Fecha" value={fmtTime(event.close_time) || '—'} />
        <DetailBox
          icon={<CalendarDays className="h-[11px] w-[11px]" />}
          label="Data"
          value={`${fmtWeekday(event.event_date)} · ${fmtDateBR(event.event_date)}`}
        />
        <DetailBox
          icon={<Wallet className="h-[11px] w-[11px]" />}
          label="Couvert"
          value={fmtCouvert(event.couvert_type, event.couvert_value, event.couvert_until)}
        />
      </div>

      {/* Line-up */}
      {lineup.length > 0 && (
        <>
          <p className="eyebrow mb-2.5">lineup</p>
          <div className={`flex flex-col gap-2 ${event.promos ? 'mb-5' : 'mb-1.5'}`}>
            {lineup.map((a, i) => {
              const full = a.attraction_id
                ? attractions.find((x) => x.id === a.attraction_id)
                : null;
              const ig = full?.instagram;
              return (
                <div
                  key={i}
                  className="flex items-center gap-3 rounded-lg border border-border px-3.5 py-3"
                >
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-primary-soft text-primary">
                    <Headphones className="h-[15px] w-[15px]" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-semibold text-sm text-foreground truncate">
                      {a.name}
                    </span>
                    {ig && (
                      <span className="flex items-center gap-1 text-[11px] text-muted-foreground mt-0.5">
                        <Instagram className="h-3 w-3" />
                        {ig}
                      </span>
                    )}
                  </span>
                  {a.time && (
                    <span className="font-mono text-xs text-muted-foreground shrink-0">
                      {fmtTime(a.time)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Destaque */}
      {event.highlight && (
        <div className="mb-5">
          <p className="eyebrow mb-1.5">destaque</p>
          <p className="text-sm font-semibold text-foreground">{event.highlight}</p>
        </div>
      )}

      {/* Promos — caixa primary-soft com PartyPopper (mono pra manter o pontilhado alinhado) */}
      {promos && (
        <div className="rounded-lg bg-primary-soft px-3.5 py-3 text-primary">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider">
              <PartyPopper className="h-4 w-4 shrink-0" />
              promoções
            </span>
            <button
              type="button"
              onClick={() => void copyPromos(promos)}
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-mono text-[11px] uppercase tracking-wider transition hover:bg-primary/10 active:scale-[0.97]"
            >
              <Copy className="h-3.5 w-3.5" /> Copiar
            </button>
          </div>
          <pre className="overflow-x-auto font-mono text-[10px] leading-relaxed sm:text-[11px]">
            {promos}
          </pre>
        </div>
      )}

      {/* Tasks vinculadas */}
      {event.tasks.length > 0 && (
        <div className="mt-5">
          <p className="eyebrow mb-2.5">
            tasks ({event.tasks.filter((t) => t.status === 'done').length}/{event.tasks.length})
          </p>
          <ul className="space-y-1.5">
            {event.tasks.map((t) => (
              <li key={t.id} className="text-sm flex items-center gap-2">
                <span
                  className={`h-1.5 w-1.5 rounded-full ${t.status === 'done' ? 'bg-success' : 'bg-primary'}`}
                />
                <span className={t.status === 'done' ? 'line-through text-muted-foreground' : ''}>
                  {t.title}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {creator && (
        <p className="text-[11px] text-muted-foreground mt-5">Criado por {creator.name}</p>
      )}

      {/* Rodape: Fechar (secondary) + Editar (primary) */}
      {canEdit ? (
        <div className="flex gap-2.5 mt-[22px]">
          <Button variant="secondary" onClick={onClose} className="flex-1">
            Fechar
          </Button>
          <Button onClick={onEdit} className="flex-1">
            <Pencil className="h-[15px] w-[15px]" /> Editar
          </Button>
        </div>
      ) : (
        <p className="text-center text-xs text-muted-foreground mt-[22px]">
          Somente o criador ou admins podem editar este evento.
        </p>
      )}
    </BottomSheet>
  );
}

// Caixa de detalhe: eyebrow com icone + valor em mono
function DetailBox({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg bg-surface-2 px-3.5 py-3">
      <p className="eyebrow text-[9px] inline-flex items-center gap-1.5">
        {icon}
        {label}
      </p>
      <p className="mt-1 font-mono text-[13px] font-semibold text-foreground">{value}</p>
    </div>
  );
}
